import MapBoxMap from 'components/mapboxmap/MapBoxMap';
import { WarehouseIcon } from 'components/svgs';
import { arrowCapacityPlanRoutesLayer } from 'components/mapSection/components/polylines/ArrowLayer';
import { capacityPlanRoutesLineLayer } from 'components/mapSection/components/polylines/LineLayer';
import {
  capacityPlanStopsCircleLayer,
  capacityPlanStopsNumberLayer,
} from 'components/mapSection/components/polylines/StopLayers';
import { PlannedRouteView } from 'core/graphql/CapacityPlanning/models';
import useAppDispatch from 'core/hooks/useAppDispatch';
import useAppSelector from 'core/hooks/useAppSelector';
import {
  selectCoordinates,
  selectZoomLevel,
  setCoordinates,
  setZoomLevel,
} from 'pages/itinerary/MapSlice';
import { useEffect, useMemo, useRef } from 'react';
import { Layer, Marker, Source } from 'react-map-gl';
import { routeColour } from './RouteColours';

export interface ResultMapProps {
  routes: PlannedRouteView[];
  depotLatitude: number | null | undefined;
  depotLongitude: number | null | undefined;
  /** Index of the only route to draw, in the order `routes` came in. */
  selectedRouteIndex?: number | null;
  /** Stop sequence to draw larger and highlighted on the map, if any. */
  focusedStopSequence?: number | null;
  /** Map instance id; give the second mounted map its own, or they collide. */
  id?: string;
}

const depotZoomLevel = 11;

/**
 * Planned routes, one colour per route, starting and ending at the Planning
 * Depot. When `selectedRouteIndex` is set, only that route is drawn.
 */
const ResultMap = ({
  routes,
  depotLatitude,
  depotLongitude,
  selectedRouteIndex,
  focusedStopSequence,
  id = 'capacityPlanResultMap',
}: ResultMapProps) => {
  const dispatch = useAppDispatch();
  const zoomLevel = useAppSelector(selectZoomLevel);
  const coordinates = useAppSelector(selectCoordinates);

  // The viewport lives in the itinerary MapSlice, which every other map in the
  // app shares, so what this map centres on is put back when it goes away.
  const viewOnMount = useRef({ coordinates, zoomLevel });
  useEffect(
    () => () => {
      if (viewOnMount.current.coordinates) {
        dispatch(setCoordinates(viewOnMount.current.coordinates));
      }
      dispatch(setZoomLevel(viewOnMount.current.zoomLevel));
    },
    [dispatch],
  );

  useEffect(() => {
    if (depotLatitude && depotLongitude) {
      dispatch(
        setCoordinates({
          latitude: depotLatitude,
          longitude: depotLongitude,
        }),
      );
      dispatch(setZoomLevel(depotZoomLevel));
    }
  }, [depotLatitude, depotLongitude, dispatch]);

  const routesGeoJson = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: routes
        .map((route, index) => ({
          type: 'Feature' as const,
          id: index,
          properties: {
            colour: routeColour(index),
            routeIndex: index,
          },
          geometry: {
            type: 'LineString' as const,
            coordinates: route.geometry,
          },
        }))
        .filter(
          (feature) =>
            selectedRouteIndex == null ||
            feature.properties.routeIndex === selectedRouteIndex,
        ),
    }),
    [routes, selectedRouteIndex],
  );

  const stopsGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    const selectedIndex = selectedRouteIndex ?? -1;
    const selectedRoute = routes[selectedIndex];

    if (!selectedRoute) {
      return { type: 'FeatureCollection', features: [] };
    }

    const colour = routeColour(selectedIndex);
    let sequence = 0;

    return {
      type: 'FeatureCollection',
      features: selectedRoute.steps
        .filter(
          (step) =>
            step.type === 'job' &&
            step.latitude != null &&
            step.longitude != null,
        )
        .map((step) => {
          sequence += 1;
          return {
            type: 'Feature' as const,
            properties: {
              sequence,
              deliveries: step.deliveryIds.length,
              colour,
              focused: sequence === focusedStopSequence,
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [step.longitude as number, step.latitude as number],
            },
          };
        }),
    };
  }, [routes, selectedRouteIndex, focusedStopSequence]);

  return (
    <MapBoxMap id={id} zoomLevel={zoomLevel} interactiveLayerIds={undefined}>
      <Source type="geojson" data={routesGeoJson}>
        <Layer {...capacityPlanRoutesLineLayer} />
        <Layer {...arrowCapacityPlanRoutesLayer} />
      </Source>
      <Source type="geojson" data={stopsGeoJson}>
        <Layer {...capacityPlanStopsCircleLayer} />
        <Layer {...capacityPlanStopsNumberLayer} />
      </Source>
      {depotLatitude && depotLongitude && (
        <Marker
          longitude={depotLongitude}
          latitude={depotLatitude}
          anchor="center"
        >
          <div
            className="flex items-center justify-center rounded-full bg-design-black p-2"
            title="Planning depot"
          >
            <WarehouseIcon className="h-5 w-5 fill-white text-white" />
          </div>
        </Marker>
      )}
    </MapBoxMap>
  );
};

export default ResultMap;
